import React, { Component, createRef } from "react";
import { connect } from "react-redux";

import { Ref } from "semantic-ui-react";

import BasePadding from "@cayuse/shared-react.components.base-padding";
import Breadcrumbs from "@cayuse/shared-react.components.breadcrumbs";
import LoadingIndicator from "@cayuse/shared-react.components.loading-indicator";
import { isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import CostShareMainView from "components/BudgetBuilder/CostShare/Main";
import OverviewMainView from "components/BudgetBuilder/Overview/Main";
import SetupMainView from "components/BudgetBuilder/Setup/Main";
import { EditingContextWrapper } from "components/BudgetBuilder/shared/EditingContext/EditingContext";
import { TenantFormatterWrapper } from "components/BudgetBuilder/shared/TenantFormatter";
import SponsorCostsMainView from "components/BudgetBuilder/SponsorCosts/Main";
import VersionsMainView from "components/BudgetBuilder/Versions/Main";
import SPRedirect from "components/SPRedirect";
import { PROJECTS_BASE_ROUTE, IS_FT, IS_ENG } from "globals/constants";
import { getAward, resetAward } from "store/actions/award";
import { loadBudgetBuilder } from "store/actions/budgetBuilder";
import { fetchPicklistOptions } from "store/actions/picklists";
import { getProposal, resetProposal } from "store/actions/proposal";
import { spUrl } from "utils";

import SectionHeaderBudgetBuilder from "./SectionHeaderBudgetBuilder";
import styles from "./styles.css";
import Tabs from "components/Tab/Tabs";

class BudgetBuilderView extends Component {
	constructor (props) {
		super(props);
		this.state = {
			isCheckingAccess: true,
			hasAccess: false,
		};
		this.tabsRef = createRef();

		// Just for Oceanside release. Should be deleted soon
		this.isDev = IS_FT || IS_ENG;
	}

	resetProposalOrAward = () => {
		if (this.isProposalBudget()) {
			resetProposal();
		} else if (this.isAwardBudget()) {
			resetAward();
		}
	}

	getProposalOrAwardUrl = () => {
		let url = "/sp";
		if (this.isProposalBudget()) {
			return `/sp/proposals/${this.props.match?.params?.proposalId}`;
		} else if (this.isAwardBudget()) {
			return `/sp/awards/${this.props.match?.params?.awardId}`;
		}

		return spUrl(url);
	}

	fetchProposalOrAward = () => {
		if (this.isProposalBudget()) {
			return getProposal(this.props.match?.params?.proposalId);
		} else if (this.isAwardBudget()) {
			return getAward(this.props.match?.params?.awardId);
		}

		return Promise.reject();
	}

	fetchBudgetBuilder = () => {
		const budgetType = this.isProposalBudget() ?
			"proposal"
			: this.isAwardBudget() ?
				"award"
				: null;

		return loadBudgetBuilder(false, budgetType);
	};

	getBudgetSummaryType = formType => {
		const formElements = this.props?.[formType]?.formElements || [];
		const budgetSummary = formElements.find(formElement =>
			formElement.cayuseType === "cayuseSummaryBudgetImporter" || formElement.type === "cayuseSummaryBudgetImporter");

		if (budgetSummary) {
			const budgetAnswer = JSON.parse(budgetSummary.answer);
			const budgetType = budgetAnswer?.inputType || "manual";

			return budgetType;
		}
	}

	shouldAccessBudgetBuilder = () => {
		let budgetType;

		if (this.isProposalBudget()) {
			budgetType = this.getBudgetSummaryType("proposal");
		} else if (this.isAwardBudget()) {
			budgetType = this.getBudgetSummaryType("award");
		}

		return budgetType === "builder";
	}

	componentDidMount () {
		const { history, budgetBuilderEnabled } = this.props;
		if (!budgetBuilderEnabled) {
			history.push(this.getProposalOrAwardUrl());
		} else {
			this.resetProposalOrAward();
			fetchPicklistOptions("organizations");
			this.fetchProposalOrAward()
				.then(() => {
					if (this.shouldAccessBudgetBuilder()) {
						this.setState({ hasAccess: true });
					}

					this.setState({ isCheckingAccess: false });
					this.fetchBudgetBuilder();
				});
		}
	}

	componentDidUpdate (prevProps, prevState, snapshot) {
		const { match: { params: { proposalId, awardId } } } = this.props;
		const { match: { params: { proposalId: prevProposalId, awardId: prevAwardId } } } = prevProps;

		if (proposalId !== prevProposalId || awardId !== prevAwardId) {
			this.resetProposalOrAward();
			this.fetchProposalOrAward()
				.then(() => this.fetchBudgetBuilder());
		}
	}

	renderBreadcrumbs = () => {
		let breadcrumbs = [];
		const dictionary = this.props.dictionary;
		if (this.isProposalBudget()) {
			breadcrumbs = BudgetBuilderView.getProposalBreadcrumbs(this.props.proposal, dictionary);
		} else if (this.isAwardBudget()) {
			breadcrumbs = BudgetBuilderView.getAwardBreadcrumbs(this.props.award, dictionary);
		}

		return (
			<Breadcrumbs breadcrumbs={breadcrumbs} />
		);
	};

	getRedirectUrl = (proposal, award) => {
		let redirectUrl = "/sp";
		if (this.isProposalBudget()) {
			redirectUrl = `/sp/proposals/${proposal?.proposalId}`;
		} else if (this.isAwardBudget()) {
			redirectUrl = `/sp/awards/${award?.awardId}`;
		}

		return spUrl(redirectUrl);
	};

	static getProposalBreadcrumbs = (proposal, dictionary) => [
		{
			key: 0,
			text: dictionary?.breadcrumbRoot || "My Active Projects",
			link: spUrl("/"),
		},
		{
			key: 1,
			text: proposal?.projectTitle,
			link: spUrl(`${PROJECTS_BASE_ROUTE}/${proposal?.projectId}`),
		},
		{
			key: 2,
			text: proposal?.proposalNumber,
			link: spUrl(`/sp/proposals/${proposal?.proposalId}`),
		},
		{
			key: 3,
			text: "Budget",
			link: spUrl(`/sp/proposals/${proposal?.proposalId}/bb`),
		},
	]

	static getAwardBreadcrumbs = (award, dictionary) => [
		{
			key: 0,
			text: dictionary?.breadcrumbRoot || "My Active Projects",
			link: spUrl("/"),
		},
		{
			key: 1,
			text: award?.projectTitle,
			link: spUrl(`${PROJECTS_BASE_ROUTE}/${award?.projectId}`),
		},
		{
			key: 2,
			text: award?.awardNumber,
			link: spUrl(`/sp/awards/${award?.awardId}`),
		},
		{
			key: 3,
			text: "Budget",
			link: spUrl(`/sp/awards/${award?.awardId}/bb`),
		},
	];

	isAwardBudget = () => isNonEmptyString(this.props.match?.params?.awardId)
	isProposalBudget = () => isNonEmptyString(this.props.match?.params?.proposalId)

	render () {
		const { dictionary, proposal, award, isLoading, isProposalLoading, isRefresh } = this.props;
		const { isCheckingAccess, hasAccess } = this.state;
		const projectTitle = this.isProposalBudget() ? proposal?.projectTitle : award?.projectTitle;
		const redirectUrl = this.getRedirectUrl(proposal, award);
		const panes = [
			{
				menuItem: "Overview",
				render: () => (
					<div loading={isLoading}>
						<OverviewMainView />
					</div>
				),
				ref: React.createRef(),
			},
			{
				menuItem: "Sponsor Costs",
				render: () => (
					<div loading={isLoading}>
						<SponsorCostsMainView />
					</div>
				),
				ref: React.createRef(),
			},
			{
				menuItem: dictionary.tabLabels?.history || "Cost Share",
				render: () => (
					<div loading={isLoading}>
						<CostShareMainView />
					</div>
				),
				ref: React.createRef(),
			},
			{
				menuItem: "Versions",
				render: () => (
					<div loading={isLoading}>
						{ this.isDev ? <VersionsMainView /> : "Coming soon" }
					</div>
				),
				ref: React.createRef(),
			},
			{
				menuItem: "Setup",
				render: () => (
					<div loading={isLoading}>
						<SetupMainView />
					</div>
				),
				ref: React.createRef(),
			},
		];
		

		return (
			isCheckingAccess ? (
				<LoadingIndicator />
			) : (
				<>
					{ !hasAccess ? (
						<SPRedirect to={redirectUrl}/>
					) : (
						<TenantFormatterWrapper>
							{isProposalLoading && !isRefresh
								? <LoadingIndicator />
								: <SectionHeaderBudgetBuilder
									header={projectTitle}
									breadcrumbs={this.renderBreadcrumbs()}
								  />
							}

							<BasePadding>
								<EditingContextWrapper>
									<Ref innerRef={this.tabsRef}>
									<Tabs 
										className={styles.budgetBuilderView}
										panes={panes}/>
									</Ref>
								</EditingContextWrapper>
							</BasePadding>
						</TenantFormatterWrapper>
					)}
				</>
			)
		);
	}
}

const mapStateToProps = (state) => ({
	budgetBuilderEnabled: state.tenant.budgetBuilderEnabled,
	dictionary: state.dictionary.budgetBuilder,
	proposal: state.proposal.content.proposal,
	award: state.award.content.award,
	isLoading: state.proposal.content.proposal.isFetching || (state.budgetBuilder.isFetching && !state.budgetBuilder.isRefresh),
	isFormLoading: state.budgetBuilder.isFetching,
	isProposalLoading: state.proposal.isFetching,
	hasProposalError: isNonEmptyString(state.proposal.content.proposal.error ?? ""),
	isRefresh: state.proposal.content.proposal.isRefresh,
});

export default connect(mapStateToProps)(BudgetBuilderView);
